import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import logo from "@/assets/logo.png?format=webp&w=200";
// Fallback for the tiny minority of browsers without WebP: force a resized
// PNG variant so we don't ship a 600 KB source PNG for a 64 px logo.
import logoFallback from "@/assets/logo.png?format=png&w=200";

const navLinks = [
  { name: "Home", path: "/" },
  { name: "Services", path: "/services" },
  { name: "Bookkeeping", path: "/bookkeeping" },
  { name: "VAT", path: "/vat" },
  { name: "CIPC", path: "/cipc" },
  { name: "Packages", path: "/packages" },
  { name: "Tools", path: "/tools" },
  
  { name: "Contact", path: "/contact" },
];

export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 w-full bg-background/95 backdrop-blur-md border-b border-border">
      <div className="container mx-auto px-4">
        <nav aria-label="Primary" className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 sm:gap-3" aria-label="Bookkeeping Services South Africa — home">
            <picture>
              <source srcSet={logo} type="image/webp" />
              <img
                src={logoFallback}
                alt=""
                aria-hidden="true"
                className="h-14 sm:h-16 w-auto"
                width={64}
                height={64}
                fetchPriority="high"
                decoding="async"
              />
            </picture>
            <div className="text-left">
              <span className="font-heading text-base sm:text-xl font-bold text-primary leading-tight block">
                Bookkeeping Services
              </span>
              <span className="block text-xs sm:text-sm font-semibold text-primary/70">South Africa</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                aria-current={isActive(link.path) ? "page" : undefined}
                className={`nav-link text-sm ${isActive(link.path) ? "nav-link-active" : ""}`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
            <Link to="/book-a-call">
              <Button className="btn-primary gap-2">
                <Phone className="w-4 h-4" />
                Book a Call
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            type="button"
            className="lg:hidden p-2 text-foreground"
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? "Close menu" : "Open menu"}
            aria-expanded={isOpen}
            aria-controls="mobile-navigation"
          >
            {isOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
          </button>
        </nav>

        {/* Mobile Navigation */}
        {isOpen && (
          <div id="mobile-navigation" className="lg:hidden py-4 border-t border-border animate-fade-in">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`nav-link py-2 ${isActive(link.path) ? "nav-link-active" : ""}`}
                  onClick={() => setIsOpen(false)}
                >
                  {link.name}
                </Link>
              ))}
              <Link to="/book-a-call" onClick={() => setIsOpen(false)}>
                <Button className="btn-primary w-full gap-2 mt-2">
                  <Phone className="w-4 h-4" />
                  Book a Call
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
